import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import type { Product } from "../../hooks/products/types";
import { buildProductUrl } from "../../utils/navigation";

type BannerHeroProps = {
  products?: Product[];
  product?: Product;
  title?: string;
  image?: string;
};

const ChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const BannerHero: React.FC<BannerHeroProps> = ({
  products = [],
  product: singleProduct,
}) => {
  const allProducts = products.length > 0 ? products : singleProduct ? [singleProduct] : [];
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const total = allProducts.length;
  const featured = total > 0 ? allProducts[currentIndex] : undefined;

  const goTo = (idx: number) => setCurrentIndex((idx + total) % total);
  const prev = () => goTo(currentIndex - 1);
  const next = () => goTo(currentIndex + 1);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (delta < -50) next();
    else if (delta > 50) prev();
    touchStartX.current = null;
  };

  // Price badge: "from $X" when variants exist, else "$X"
  const hasVariantPrice = featured?.has_variants && featured?.min_variant_price != null;
  const priceAmount = hasVariantPrice ? featured!.min_variant_price! : featured?.price;

  const nameParts = featured?.name?.split(" ") ?? [];
  const titleFirst = nameParts.length > 1 ? nameParts.slice(0, -1).join(" ") : nameParts[0] ?? "";
  const titleSecond = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";

  const viewAllTo = featured?.brand_id
    ? `/products?brand_id=${featured.brand_id}`
    : "/products";
  const viewAllLabel = featured?.brand_name
    ? `Shop ${featured.brand_name}`
    : "View All";

  const brandLabel = featured ? (
    <div className="text-[10px] text-header-muted tracking-[3.5px] uppercase mb-2 font-medium">
      {featured.brand_name ?? ""}
    </div>
  ) : (
    <div className="h-3 w-20 bg-section-skeleton animate-pulse rounded mb-2" />
  );

  const titleBlock = featured ? (
    <>
      <h1
        className="font-normal text-primary leading-[0.95] mb-1 line-clamp-2"
        style={{ fontFamily: "var(--font-display)", fontSize: "clamp(44px, 8vw, 68px)" }}
      >
        {titleFirst}
      </h1>
      {titleSecond && (
        <h2
          className="font-light italic text-primary leading-[0.95]"
          style={{ fontFamily: "var(--font-display)", fontSize: "clamp(44px, 8vw, 68px)" }}
        >
          {titleSecond}
        </h2>
      )}
    </>
  ) : (
    <div className="space-y-2">
      <div className="h-14 w-64 bg-section-skeleton animate-pulse rounded" />
      <div className="h-14 w-48 bg-section-skeleton animate-pulse rounded" />
    </div>
  );

  const dots = total > 1 ? (
    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-[5px]">
      {allProducts.map((_, i) => (
        <button
          key={i}
          onClick={() => goTo(i)}
          className="h-[6px] rounded-[3px] transition-all duration-200"
          style={{
            width: i === currentIndex ? 16 : 6,
            background: i === currentIndex ? "var(--color-primary)" : "rgba(0,0,0,0.2)",
          }}
        />
      ))}
    </div>
  ) : null;

  return (
    <div className="w-full bg-white border-t border-header-border">

      {/* ── Mobile layout (< lg) ── */}
      <div
        className="lg:hidden"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* Product image area */}
        <div className="relative h-[280px] overflow-hidden">
          {featured?.image ? (
            <img
              key={featured.id}
              src={featured.image}
              alt={featured.name}
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#f5f1ea] via-[#e6ddd0] to-[#d8ccba] flex items-center justify-center">
              {featured === undefined
                ? <div className="w-full h-full bg-section-skeleton animate-pulse" />
                : <span className="text-[9px] text-black/30 tracking-[2px] uppercase">product photography</span>}
            </div>
          )}

          {/* Price badge — bottom right */}
          {priceAmount != null && (
            <div className="absolute bottom-4 right-4 bg-white border border-header-border px-[15px] py-[9px] shadow-md">
              {hasVariantPrice && (
                <div className="text-[8.5px] text-header-muted tracking-[1.5px] uppercase mb-[2px]">From</div>
              )}
              <div
                className="text-[22px] font-semibold text-primary leading-none"
                style={{ fontFamily: "var(--font-display)" }}
              >
                ${priceAmount}
              </div>
            </div>
          )}

          {/* Prev / Next arrows — mobile */}
          {total > 1 && (
            <>
              <button
                onClick={prev}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 border border-header-border p-2 text-accent"
              >
                <ChevronLeft />
              </button>
              <button
                onClick={next}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 border border-header-border p-2 text-accent"
              >
                <ChevronRight />
              </button>
            </>
          )}

          {dots}
        </div>

        {/* Text content */}
        <div className="px-5 pt-[26px] pb-8" key={featured?.id}>
          {brandLabel}
          <div className="mb-5">{titleBlock}</div>
          <div className="w-11 h-px bg-accent mb-[18px]" />

          {featured ? (
            <p className="text-[13px] text-header-muted leading-[1.75] mb-[26px] line-clamp-3">
              {featured.description}
            </p>
          ) : (
            <div className="mb-6.5 space-y-2">
              <div className="h-3 w-full bg-section-skeleton animate-pulse rounded" />
              <div className="h-3 w-4/5 bg-section-skeleton animate-pulse rounded" />
            </div>
          )}

          {featured && (
            <div className="flex gap-2.5">
              <Link
                to={buildProductUrl(featured.id, featured.name, featured.brand_name)}
                className="flex-1 py-3.5 bg-primary text-white text-center text-[11px] font-medium tracking-[2px] uppercase hover:bg-primary-light transition-colors"
              >
                Shop Now
              </Link>
              <Link
                to={viewAllTo}
                className="flex-1 py-[14px] border border-header-border text-primary text-center text-[11px] tracking-[1.5px] uppercase hover:border-primary transition-colors"
              >
                {viewAllLabel}
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* ── Desktop layout (>= lg) ── */}
      <div className="hidden lg:flex h-125 max-w-7xl mx-auto border border-header-border overflow-hidden">
        {/* Left: text */}
        <div className="flex-1 flex flex-col px-16 py-14 overflow-hidden" key={featured?.id}>

          {/* TOP — always pinned to top */}
          <div className="shrink-0">
            {brandLabel}
          </div>

          {/* MIDDLE — stretches to fill available space, content centered within */}
          <div className="flex-1 flex flex-col justify-center min-h-0">
            <div className="mb-7">{titleBlock}</div>
          </div>

          {/* BOTTOM — always pinned to bottom */}
          <div className="shrink-0">
            {featured && (
              <div className="flex items-center gap-3 flex-wrap mb-8">
                <Link
                  to={buildProductUrl(featured.id, featured.name, featured.brand_name)}
                  className="px-8.5 py-3.25 bg-primary text-white text-[11px] font-medium tracking-[2px] uppercase hover:bg-primary-light transition-colors"
                >
                  Shop Now
                </Link>
                <Link
                  to={viewAllTo}
                  className="px-5.5 py-3.25 border border-header-border text-primary text-[11px] tracking-[1.5px] uppercase hover:border-primary transition-colors"
                >
                  {viewAllLabel}
                </Link>
              </div>
            )}
            {total > 1 && (
              <div className="flex items-center gap-3">
                <button
                  onClick={prev}
                  className="border border-header-border p-1.5 px-4 text-accent hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft />
                </button>
                <div className="flex gap-1.25">
                  {allProducts.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => goTo(i)}
                      className="h-1.5 rounded-[3px] transition-all duration-200"
                      style={{
                        width: i === currentIndex ? 16 : 6,
                        background: i === currentIndex ? "var(--color-primary)" : "rgba(0,0,0,0.15)",
                      }}
                    />
                  ))}
                </div>
                <button
                  onClick={next}
                  className="border border-header-border p-1.5 px-4 text-accent hover:bg-gray-50 transition-colors"
                >
                  <ChevronRight />
                </button>
              </div>
            )}
          </div>

        </div>

        {/* Right: product image */}
        <div className="w-95 shrink-0 border-l border-header-border relative overflow-hidden">
          {/* Price card */}
          {priceAmount != null && (
            <div className="absolute top-7 left-6 z-10 bg-white border border-header-border px-4 py-[10px] shadow-md">
              {hasVariantPrice && (
                <div className="text-[9px] text-header-muted tracking-[2px] uppercase mb-[3px]">From</div>
              )}
              <div
                className="text-[24px] font-semibold text-primary leading-none"
                style={{ fontFamily: "var(--font-display)" }}
              >
                ${priceAmount}
              </div>
            </div>
          )}

          {/* Image */}
          {featured?.image ? (
            <img key={featured.id} src={featured.image} alt={featured.name} className="w-full h-full object-contain p-4" />
          ) : featured ? (
            <div className="w-full h-full bg-linear-to-br from-[#f5f1ea] via-[#e6ddd0] to-[#d8ccba] flex items-center justify-center">
              <span className="text-[9px] text-black/30 tracking-[2px] uppercase">product photography</span>
            </div>
          ) : (
            <div className="w-full h-full bg-section-skeleton animate-pulse" />
          )}
        </div>
      </div>

    </div>
  );
};

export default BannerHero;
